<template>
  <section class="section-confirm-bank-transfer content-padding-gray">
    <!-- Navigation -->
    <div class="row mb-3">
      <div class="col-12 col-md-8 offset-md-2">
        <router-link to="/my-reservations" class="text-primary-link">
          {{ $t('message.menu.my_reservations') }}
        </router-link>
        <i class="fal fa-angle-right"></i>
        <router-link class="text-primary-link" :to="`/reservation/${orderId}`">
          {{ $t('message.my_reservations.reservation_detail') }}
        </router-link>
        <i class="fal fa-angle-right"></i>
        {{ $t('message.my_reservations.payment') }}
      </div>
    </div>

    <template v-if="order.bank_transfer_data">
      <div class="row success-booking">
        <div class="col-12 col-lg-6 offset-lg-3">
          <div class="text-center p-2 text-center">
            <img src="/static/img/ic-waiting-confirmation.svg" width="90" v-if="order.paid_status === 0">
            <i class="fal fa-check-circle fa-5x -status-accepted" v-else></i>
            <h2 class="pt-4 m-0">
              {{ order.order_summary_total.total | currency }}
            </h2>
          </div>
          <div class="card">
            <div class="card-body">
              <div>
                <span v-if="order.paid_status === 0">
                  <b>{{ $t('message.bank_transfer.complete_bank_transfer') }}</b>
                </span>
                <span v-else>
                  <b>{{ $t('message.qr_payment.payment_successful') }}</b>
                </span>
              </div>
              <hr>
              <div class="row no-gutters pb-2">
                <div class="col-6">{{ $t('message.qr_payment.payment_method') }}</div>
                <div class="col-6 text-right">{{ $t('message.payment_method.bank_transfer') }}</div>
              </div>
              <div class="row no-gutters pb-2" v-if="order.order_summary_total.reconcile_date">
                <div class="col-6">{{ $t('message.qr_payment.transaction_date') }}</div>
                <div class="col-6 text-right">{{ order.order_summary_total.reconcile_date | prettyDate }}</div>
              </div>
              <div class="row no-gutters">
                <div class="col-6">{{ $t('message.qr_payment.order_id') }}</div>
                <div class="col-6 text-right">#{{ order.order_code }}</div>
              </div>
            </div>
          </div>
          <div class="pt-2">
            <router-link :to="'/reservation/'+order.id" tag="a" class="btn btn-info btn-block">
              {{ $t('message.booking.complete_step.view_my_booking') }}
            </router-link>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <template v-if="order.is_expired_bank_transfer === 1">
        <div class="row">
          <div class="col-lg-6 offset-lg-3">
            <div class="card">
              <div class="card-body text-center">
                <i class="fal fa-frown fa-4x -status-new"></i>
                <div class="mt-3 mb-3">
                  <h4>{{ $t('message.my_reservations.booking') }} #{{ order.order_code }}</h4>
                  {{ $t('message.bank_transfer.expired_bank_transfer') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="row">
          <div class="col-md-8 offset-md-2">
            <div class="card" v-if="globalState">
              <div class="card-body">
                <h3 class="text-center py-2">{{ $t('message.bank_transfer.title') }}</h3>
                <span v-html="$options.filters.bankTransferDurationText(order, globalState)"></span>
                <div class="mt-3" v-if="order.selected_professional_id > 0">
                  <img v-lazy="order.professional_picture" class="rounded-circle" width="40">
                  &nbsp;{{ order.professional_name | firstName }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-8 offset-md-2">
            <div class="card">
              <div class="card-body">
                <template v-if="order">
                  <section class="mt-3">
                    <div class="text-left mt-4">
                      <order-summary-detail :order="order"/>
                    </div>
                    <order-summary-total-short :order="order"/>
                  </section>
                  <hr>
                </template>
                <section class="mt-4">
                  <div class="row mb-3">
                    <div class="col">
                      <h4>{{ $t('message.bank_transfer.detail') }}</h4>
                    </div>
                  </div>

                  <div class="row mb-3">
                    <div class="col-12 col-md-3">
                      <label>{{ $t('message.bank_transfer.transfer_account') }}</label>
                    </div>
                    <div class="col-12 col-md-9">
                      <div>
                        <img :src="globalState.bankAccountData.image_url" width="160" class="mb-2">
                      </div>
                      <div>{{ $t('message.bank_transfer.account_name') }}: {{ globalState.bankAccountData | multiLangName }}
                      </div>
                      <div class="d-flex align-items-center justify-content-center">
                        <div class="flex-grow-1">
                          <div>{{ $t('message.bank_transfer.account') }}: {{ globalState.bankAccountData.account_number }}
                          </div>
                        </div>
                        <div class="text-right">
                          <button class="btn btn-outline-info"
                                  @click="isCopied = true"
                                  v-clipboard:copy="globalState.bankAccountData.account_number">
                            {{ isCopied ? $t('message.button.copied') : $t('message.button.copy') }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="row mb-3">
                    <div class="col-12 col-md-3">
                      <label>{{ $t('message.bank_transfer.transfer_picture') }}</label>
                      <span class="text-danger">*</span>
                    </div>
                    <div class="col-12 col-md-9">
                      <div v-if="!uploadSuccess">
                        <vue-dropzone ref="vueDropzone"
                                      id="dropzone"
                                      class="dropzone-dashed"
                                      :options="dropzoneOptions"
                                      @vdropzone-max-files-exceeded="vueDropZoneMaxFilesExceeded"
                                      @vdropzone-mounted="vueDropZoneMounted"
                                      @vdropzone-success="vueDropZoneSuccess"/>
                        <div class="text-remark py-2">
                          {{ $t('message.bank_transfer.transfer_picture_remark01') }}
                        </div>

                        <button class="btn btn-info btn-block" @click="performUpload()">
                          {{ $t('message.button.submit') }}
                        </button>
                      </div>
                      <div v-else>
                        <i class="fas fa-check-circle -status-accepted"></i>
                        {{ $t('message.bank_transfer.upload_success') }}
                      </div>

                    </div>
                  </div>

                </section>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </section>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import OrderSummaryDetail from './OrderSummaryDetail'
  import OrderSummaryTotalShort from './components/OrderSummaryTotalShort'
  import vue2Dropzone from 'vue2-dropzone'
  import insiderHelpers from '../../helpers/insiderHelpers'

  export default {
    components: {
      OrderSummaryDetail,
      OrderSummaryTotalShort,
      vueDropzone: vue2Dropzone
    },
    data () {
      return {
        orderId: this.$route.params.id,
        bankTransferId: null,
        transferAmount: null,
        transferPicture: null,
        hours: null,
        minutes: null,
        isCopied: false,
        uploadSuccess: false,
        dropzoneOptions: {
          acceptedFiles: 'image/*',
          url: 'https://beneat.co',
          thumbnailWidth: 160,
          thumbnailHeight: 160,
          maxFilesize: 5,
          dictDefaultMessage: `<i class="fal fa-cloud-upload"></i> ${this.$i18n.t('message.button.attach_slip_here')}`,
          addRemoveLinks: true,
          autoProcessQueue: false,
          maxFiles: 1
        }
      }
    },
    computed: {
      ...mapGetters({
        authState: 'auth/getState',
        globalState: 'globalVal/getState',
        order: 'orderDetail/getOrderDetail',
        user: 'auth/getUser',
        userPartner: 'partner/getUserPartner'
      })
    },
    watch: {
      order () {
        if (this.order) {
          this.transferAmount = Math.round(this.order.order_summary_total.total * 100) / 100
        }
      }
    },
    methods: {
      ...mapActions('auth', ['getToken']),
      ...mapActions('orderDetail', ['loadOrderDetail']),
      async fetchOrderDetail () {
        if (this.user) {
          await this.loadOrderDetail(this.orderId)
        } else {
          const submitData = {
            order_id: this.orderId,
            validation: this.$route.query.validation
          }
          await this.getToken(submitData)
          await this.loadOrderDetail(this.orderId)
        }
      },
      pad (num) {
        return (num < 10) ? '0' + num.toString() : num.toString()
      },
      generateTime () {
        this.hours = []
        for (let i = 0; i <= 23; i++) {
          this.hours[i] = this.pad(i)
        }
        this.minutes = []
        for (let i = 0; i <= 59; i++) {
          this.minutes[i] = this.pad(i)
        }
      },
      finishConfirmBankTransfer () {
        if (this.userPartner) {
          this.$swal({text: this.$i18n.t('message.alert.please_wait_sending_data')})
          const submitData = {
            return_url: this.userPartner.return_url,
            token: this.userPartner.token,
            status: 'COMPLETED',
            lang: this.$i18n.locale,
            order_id: this.order.id.toString()
          }
          this.$store.dispatch('partner/SEND_COMPLETE_BOOKING', submitData)
        } else {
          this.fetchOrderDetail()
        }
      },
      performUpload () {
        this.$refs.vueDropzone.processQueue()
      },
      vueDropZoneMaxFilesExceeded (file) {
        this.$refs.vueDropzone.removeAllFiles()
        this.$refs.vueDropzone.addFile(file)
      },
      vueDropZoneMounted () {
        const uploadUrl = `${this.$http.options.root}/orders/${this.orderId}/save-bank-transfer-picture`
        this.$refs.vueDropzone.options.url = uploadUrl
        this.$refs.vueDropzone.setOption('url', uploadUrl)
        const headers = {'Authorization': `Bearer ${this.authState.token}`}
        this.$refs.vueDropzone.options.headers = headers
        this.$refs.vueDropzone.setOption('headers', headers)
      },
      vueDropZoneSuccess (file, response) {
        if (!response.error) {
          this.uploadSuccess = true
          this.transferPicture = response.image_url
          this.bankTransferId = response.bank_transfer_id
          this.finishConfirmBankTransfer()
          // Insider purchase event case bank transfer payment
          const params = {
            user: this.user,
            orderId: this.orderId
          }
          insiderHelpers.purchaseEvent(params)
        }
      }
    },
    mounted () {
      this.$store.commit('orderDetail/SET_ORDER_DETAIL', null)
      this.generateTime()
      this.fetchOrderDetail()
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/order/ConfirmBankTransfer.vue